import { FC, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { ItemPrescricao } from '../../core/montagem/itens'
import { useAssinaturaDocumentos } from '../../hooks/useAssinaturaDocumentos'
import { useCertificadoAssinatura } from '../../hooks/useCertificadoAssinatura'
import styles from '../../Prescricao.module.scss'
import { useRotulos } from '../comum/useRotulos'
import { EtapaCertificado } from './EtapaCertificado'
import { EtapaProgresso } from './EtapaProgresso'

const ESPERA_ANTES_DO_RESULTADO_MS = 700

type IProps = {
  aberto: boolean
  itens: ItemPrescricao[]
  quantidadeDocumentos: number
  assinatura: ReturnType<typeof useAssinaturaDocumentos>
  /** Fechou antes de gravar qualquer coisa: volta para a montagem com os itens na tela. */
  onCancelar: () => void
  /** Terminou (todos assinados, ou o médico seguiu sem os que deram erro). */
  onConcluir: () => void
}

/**
 * Uma senha (ou uma autorização em nuvem) para todos os documentos, depois o progresso de cada
 * um. Os rascunhos só são criados quando o certificado já está pronto: cancelar antes disso
 * não grava nada. Durante a assinatura o modal não fecha.
 */
export const AssinaturaModal: FC<IProps> = ({
  aberto,
  itens,
  quantidadeDocumentos,
  assinatura,
  onCancelar,
  onConcluir,
}) => {
  const { t } = useRotulos()
  const [etapa, setEtapa] = useState<'certificado' | 'progresso'>('certificado')
  const certificado = useCertificadoAssinatura(aberto && etapa === 'certificado')
  const { documentos, concluido, emAndamento } = assinatura
  const tudoAssinado = concluido && documentos.every((doc) => doc.estado === 'assinado')
  // O timer abaixo não pode reiniciar a cada render do pai: lê o callback mais recente por ref.
  const onConcluirRef = useRef(onConcluir)
  onConcluirRef.current = onConcluir
  const tituloRef = useRef<HTMLHeadingElement>(null)

  // "Assinar" some junto com a etapa do certificado: o foco iria para o body. Vai para o título.
  useEffect(() => {
    if (aberto && etapa === 'progresso') tituloRef.current?.focus()
  }, [aberto, etapa])

  // Cada abertura começa no certificado.
  useEffect(() => {
    if (aberto) setEtapa('certificado')
  }, [aberto])

  // Tudo assinado: mostra o resultado depois de um instante, para o médico ver o 100%.
  useEffect(() => {
    if (!aberto || etapa !== 'progresso' || !tudoAssinado) return
    const timer = setTimeout(() => onConcluirRef.current(), ESPERA_ANTES_DO_RESULTADO_MS)
    return () => clearTimeout(timer)
  }, [aberto, etapa, tudoAssinado])

  const comecar = () => {
    setEtapa('progresso')
    assinatura.assinar(itens, certificado.tipo)
  }

  const podeFechar = etapa === 'certificado' || !emAndamento
  const fechar = () => {
    if (!podeFechar) return
    if (etapa === 'progresso' && documentos.some((doc) => doc.estado === 'assinado')) onConcluir()
    else onCancelar()
  }

  // Anúncios do progresso numa região sempre montada (a do EtapaProgresso nasceria preenchida, e
  // leitores de tela costumam ignorar o conteúdo inicial). Falha parcial vai em `alert`, à parte.
  const assinados = documentos.filter((doc) => doc.estado === 'assinado').length
  const comErro = documentos.filter((doc) => doc.estado === 'erro').length
  const parcial = concluido && comErro > 0
  const anuncio =
    etapa !== 'progresso' || parcial
      ? ''
      : assinatura.preparando
      ? t('prescricao.assinatura.preparando')
      : tudoAssinado
      ? t('prescricao.assinatura.todos_assinados', { quantidade: documentos.length })
      : documentos.length > 0
      ? t('prescricao.assinatura.contagem', { assinados, total: documentos.length })
      : ''

  const titulo =
    etapa === 'certificado'
      ? t('prescricao.assinatura.titulo')
      : tudoAssinado
      ? t('prescricao.assinatura.titulo_ok')
      : t('prescricao.assinatura.titulo_andamento')

  return (
    <Modal
      show={aberto}
      onHide={fechar}
      backdrop={podeFechar ? true : 'static'}
      keyboard={podeFechar}
      // Quem abriu foi o drawer, que já fechou: o foco é decidido pela página (resultado ou revisar).
      restoreFocus={false}
      centered
      size='lg'
      contentClassName={styles.tema}
      aria-labelledby='assinatura-titulo'
    >
      <Modal.Header closeButton={podeFechar} closeLabel={t('common.close')}>
        <Modal.Title
          as='h2'
          id='assinatura-titulo'
          ref={tituloRef}
          tabIndex={-1}
          className='fs-4 fw-bold'
        >
          {titulo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div role='status' className='visually-hidden'>
          {anuncio}
        </div>
        <div role='alert' className='visually-hidden'>
          {etapa === 'progresso' && parcial
            ? t('prescricao.assinatura.parcial', { assinados, erros: comErro })
            : ''}
        </div>
        {etapa === 'certificado' ? (
          <EtapaCertificado
            certificado={certificado}
            quantidade={quantidadeDocumentos}
            onCancelar={onCancelar}
            onPronto={comecar}
          />
        ) : (
          <EtapaProgresso
            documentos={documentos}
            preparando={assinatura.preparando}
            erroPreparo={assinatura.erroPreparo}
            rascunhosNaoApagados={assinatura.rascunhosNaoApagados}
            onTentarDeNovo={assinatura.tentarDeNovo}
            onRepetirPreparo={() => assinatura.assinar(itens, certificado.tipo)}
            onContinuarSemErros={onConcluir}
            onFechar={onCancelar}
          />
        )}
      </Modal.Body>
    </Modal>
  )
}
